import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faCheckCircle, faTrash, faUser, faTimesCircle, faUserPlus,
  faUsers, faPhone, faEdit, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import EmployeeFormModal from '../components/EmployeeFormModal';
import { PERMISSION_META, hasPermission } from '../config/permissions';
import { usePermissions } from '../hooks/usePermissions';
import ActionOtpModal from '../components/shared/ActionOtpModal';
import { UiPageHeader, UiButton } from '../components/ui';

const EmployeesPage: React.FC = () => {
  const { t } = useTranslation();
  // พนักงานที่มี can_manage_employees เห็นรายชื่อได้อย่างเดียว — สร้าง/แก้/ลบ เฉพาะเจ้าของ
  const { isOwner } = usePermissions();
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // null = modal ปิด, 'new' = create mode, object = edit mode
  const [modalEmployee, setModalEmployee] = useState<any | 'new' | null>(null);

  // Action OTP
  const [showToggleOtp, setShowToggleOtp] = useState(false);
  const [showDeleteOtp, setShowDeleteOtp] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{ id: string; currentStatus: boolean } | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const loadEmployees = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/employees');
      setEmployees(response.data.data || []);
    } catch {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleToggleActive = (id: string, currentStatus: boolean) => {
    setPendingToggle({ id, currentStatus });
    setShowToggleOtp(true);
  };

  const handleToggleVerified = async (actionToken: string) => {
    if (!pendingToggle) return;
    try {
      await merchantApi.put(`/merchants/employees/${pendingToggle.id}`, {
        is_active: !pendingToggle.currentStatus,
        action_token: actionToken
      });
      toast.success(pendingToggle.currentStatus ? t('employees.suspended') : t('employees.activated'));
      setPendingToggle(null);
      loadEmployees();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = (id: string) => {
    setPendingDeleteId(id);
    setShowDeleteOtp(true);
  };

  const handleDeleteVerified = async (actionToken: string) => {
    if (!pendingDeleteId) return;
    try {
      await merchantApi.delete(`/merchants/employees/${pendingDeleteId}`, {
        data: { action_token: actionToken }
      });
      toast.success(t('employees.deleteSuccess'));
      setPendingDeleteId(null);
      loadEmployees();
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faUsers}
        title="Employee Management"
        subtitle={t('employees.desc')}
        actions={
          isOwner ? (
            <UiButton icon={faUserPlus} onClick={() => setModalEmployee('new')}>
              {t('employee.addEmployee')}
            </UiButton>
          ) : undefined
        }
      />

      {/* Employees Grid */}
      {employees.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-16 text-center">
          <FontAwesomeIcon icon={faUsers} className="text-6xl text-gray-300 dark:text-slate-600 mb-4" />
          <p className="text-gray-500 dark:text-slate-400 text-lg">{t('employees.noEmployees')}</p>
          {isOwner && (
            <div className="mt-4 flex justify-center">
              <UiButton icon={faUserPlus} onClick={() => setModalEmployee('new')}>
                {t('employees.addFirst')}
              </UiButton>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {employees.map((emp) => (
            <div
              key={emp.id}
              className={`bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden border-t-4 transition-all hover:shadow-lg ${
                emp.is_active ? 'border-green-500' : 'border-gray-300 dark:border-slate-600'
              }`}
            >
              {/* Card Header */}
              <div className="p-5 border-b border-gray-100 dark:border-slate-700">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white ${
                      emp.is_active ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gray-400 dark:bg-slate-600'
                    }`}>
                      {emp.full_name?.charAt(0).toUpperCase() || 'E'}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 dark:text-slate-100">{emp.full_name}</h3>
                      <p className="text-sm text-gray-500 dark:text-slate-400">{emp.email}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    emp.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                  }`}>
                    <FontAwesomeIcon icon={emp.is_active ? faCheckCircle : faTimesCircle} />
                    {emp.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <FontAwesomeIcon icon={faBriefcase} className="text-gray-400 dark:text-slate-500 w-4" />
                  <span className="text-gray-600 dark:text-slate-400">Role:</span>
                  <span className={`font-medium ${emp.role === 'manager' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-800 dark:text-slate-100'}`}>
                    {emp.role === 'manager' ? 'Manager' : 'Employee'}
                  </span>
                </div>

                {emp.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-400 dark:text-slate-500 w-4" />
                    <span className="text-gray-600 dark:text-slate-400">Phone:</span>
                    <span className="text-gray-800 dark:text-slate-100">{emp.phone}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <FontAwesomeIcon icon={faUser} className="text-gray-400 dark:text-slate-500 w-4" />
                  <span className="text-gray-600 dark:text-slate-400">Last Login:</span>
                  <span className="text-gray-800 dark:text-slate-100">
                    {emp.last_login_at
                      ? new Date(emp.last_login_at).toLocaleString('th-TH')
                      : t('employees.never')}
                  </span>
                </div>

                {/* Permissions */}
                <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-2 font-medium">Permissions:</p>
                  <div className="flex flex-wrap gap-2">
                    {PERMISSION_META.filter(({ key }) => hasPermission(emp.permissions, key)).map(({ key, labelKey, icon, badgeClass }) => (
                      <span key={key} className={`px-2 py-1 ${badgeClass} rounded text-xs font-medium flex items-center gap-1`}>
                        <FontAwesomeIcon icon={icon} /> {t(labelKey)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card Actions — เฉพาะเจ้าของบัญชี */}
              {isOwner && (
              <div className="px-5 py-4 bg-gray-50 dark:bg-slate-700/50 flex gap-2">
                <button
                  onClick={() => handleToggleActive(emp.id, emp.is_active)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    emp.is_active
                      ? 'bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                      : 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400'
                  }`}
                >
                  <FontAwesomeIcon icon={emp.is_active ? faBan : faCheckCircle} />
                  {emp.is_active ? t('employees.suspend') : t('employees.activate')}
                </button>
                <button
                  onClick={() => setModalEmployee(emp)}
                  className="py-2 px-3 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDelete(emp.id)}
                  className="py-2 px-3 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shared Form Modal (create + edit) */}
      {modalEmployee !== null && (
        <EmployeeFormModal
          employee={modalEmployee === 'new' ? undefined : modalEmployee}
          onClose={() => setModalEmployee(null)}
          onSuccess={loadEmployees}
        />
      )}

      <ActionOtpModal
        isOpen={showToggleOtp}
        onClose={() => { setShowToggleOtp(false); setPendingToggle(null); }}
        onVerified={handleToggleVerified}
        action="employee_update"
        title={pendingToggle?.currentStatus ? t('employees.suspendTitle') : t('employees.activateTitle')}
        description={pendingToggle?.currentStatus ? t('employees.suspendConfirm') : t('employees.activateConfirm')}
      />

      <ActionOtpModal
        isOpen={showDeleteOtp}
        onClose={() => { setShowDeleteOtp(false); setPendingDeleteId(null); }}
        onVerified={handleDeleteVerified}
        action="employee_delete"
        title={t('employees.deleteTitle')}
        description={t('employees.deleteConfirm')}
      />
    </div>
  );
};

export default EmployeesPage;
